import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IPagination } from "../../data/AppInterface";
import {
  deleteIcon,
  editIcon,
  eyeIcon,
  filterIcon,
} from "../../assets/icons/SvgIconList";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import {
  activeInactiveLookup,
  deleteLookup,
  getAllLookups,
  getLookupFields,
  ILookup,
} from "./LookupListController";
import { LOOKUP_CATEGORY_OPTION, ORDER_BY } from "../../utils/AppConstants";
import LookupForm from "./LookupForm";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  prepareMessageFromParams,
  toggleStatus,
} from "../../utils/AppFunctions";
import { ICDropDown } from "../../core-component/form";

let timer: ReturnType<typeof setTimeout>;

const LookupListView = () => {
  const { t } = UseTranslationHook();
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [enterpriseSearch, setEnterpriseSearch] = useState("");
  const [lookupList, setLookupList] = useState<ILookup[]>([]);
  const [lookupToEdit, setLookupToEdit] = useState<ILookup>();
  const [advanceSearch, setAdvanceSearch] = useState<string>("");
  const { pagination, setChangePagination } = UsePaginationHook();
  useEffect(() => {
    fetchLookup({ ...pagination, currentPage: 1 });
  }, [enterpriseSearch, advanceSearch]);

  const fetchLookup = (paginationPlayLoad: IPagination) => {
    setIsLoading(true);
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(async () => {
      await getAllLookups(
        setLookupList,
        paginationPlayLoad,
        setChangePagination,
        enterpriseSearch,
        advanceSearch,
      );
      setIsLoading(false);
    }, 500);
  };

  const handleLookupStatus = async (values: ILookup) => {
    const isStatusChange = await activeInactiveLookup(
      values.id,
      values.is_active === BIT_VALUE.One ? BIT_VALUE.Zero : BIT_VALUE.One,
    );
    if (isStatusChange) {
      setLookupList(toggleStatus(lookupList, values.id));
    }
  };

  const customActionView = (item: ILookup) => {
    return (
      <div className="flex item-center justify-end w-full">
        <div
          className="size-5 cursor-pointer"
          title={t("view_tooltip_icon")}
          onClick={() => {
            setLookupToEdit(item);
            setReadOnly(true);
            setShowForm(true);
          }}
        >
          {eyeIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("edit_tooltip_icon")}
          onClick={() => {
            setShowForm(true);
            setLookupToEdit(item);
          }}
        >
          {editIcon}
        </div>
        <div
          className="ml-2 size-5 cursor-pointer"
          title={t("delete_tooltip_icon")}
          onClick={() => {
            setShowDeleteConfirmation(true);
            setLookupToEdit(item);
          }}
        >
          {deleteIcon}
        </div>
      </div>
    );
  };

  const handleDelete = async (value: boolean) => {
    if (value && lookupToEdit) {
      const data = await deleteLookup(lookupToEdit.id);
      if (data) {
        fetchLookup(pagination);
      }
    }
    setShowDeleteConfirmation(false);
    setLookupToEdit(undefined);
  };

  const handleCloseForm = (status: boolean) => {
    setShowForm(false);
    setReadOnly(false);
    if (lookupToEdit) {
      setLookupToEdit(undefined);
    }
    if (status) {
      fetchLookup(pagination);
    }
  };

  const filterView = (
    <div className="w-full grid grid-cols-4">
      <ICDropDown
        placeholder={t("lookup_category")}
        selected={advanceSearch}
        options={LOOKUP_CATEGORY_OPTION}
        onSelect={(option) => setAdvanceSearch(option.data_value)}
      />
      <div className="!py-0 ml-4">
        <ICButton onClick={() => setAdvanceSearch("")} className="flex !w-fit">
          <div className="size-5 grid place-self-center">{filterIcon}</div>
          &nbsp;{t("clear")}
        </ICButton>
      </div>
    </div>
  );

  return (
    <div className="w-full overflow-auto scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowForm(true)}
        handleSearchChange={(text) => {
          setEnterpriseSearch(text);
        }}
        filterNode={filterView}
        searchText={enterpriseSearch}
        refreshTable={() => {
          fetchLookup(pagination);
        }}
      />

      <ICTable<ILookup>
        isLoading={isLoading}
        totalFlex="grid grid-cols-12"
        fields={getLookupFields(customActionView, handleLookupStatus, t)}
        data={lookupList}
        pagination={pagination}
        handleChangePerPageRows={(perPageRow) =>
          fetchLookup({
            ...pagination,
            perPageRows: perPageRow,
            currentPage: 1,
          })
        }
        handleChangePage={(page) =>
          fetchLookup({ ...pagination, currentPage: page })
        }
        handleSort={(field) =>
          fetchLookup({
            ...pagination,
            sortBy: field.key,
            orderBy:
              pagination.orderBy === ORDER_BY.desc
                ? ORDER_BY.asc
                : ORDER_BY.desc,
          })
        }
      />

      {showForm && (
        <LookupForm
          handleClose={handleCloseForm}
          lookupToEdit={lookupToEdit}
          readOnly={readOnly}
        />
      )}
      {showDeleteConfirmation && lookupToEdit && (
        <ICConfirmationModal
          title={t("delete_lookup")}
          message={prepareMessageFromParams(t("delete_message"), [
            ["fieldName", lookupToEdit.display_value],
          ])}
          onClose={(value) => handleDelete(value)}
        />
      )}
    </div>
  );
};

export default LookupListView;
